import { useMutation } from "react-query";

import { useApi } from "../../providers/ApiProvider";
import { APQueryOptions, useAPQuery } from "../../providers/APQueryProvider";
import {
  QuotePayPeriod,
  QuoteWithVehicleSpecification,
  VehiclePurchaseType,
} from "../../types/Quote";
import { State } from "../../types/State";

export type PatchQuotePayload = {
  dossierId?: string;
  name?: string;
  payPeriod?: QuotePayPeriod;
  purchaseType?: VehiclePurchaseType;
  state?: State;
  variantId?: string;
  term?: number;
  annualKilometres?: number;
  salary?: number;
  businessUsage?: number;
  discount?: number;
  initialOdometer?: number;
  listPrice?: number;
  optionalEquipment?: string[];
  inclusions?: string[];
  referralCode?: string;
  aiQueryId?: string;
  vehicleReviewAIQueryId?: string;
  vehicleSearchEventId?: string;
  quoteComparisonIds?: string[];
  vehicleComparisonViewId?: string;
};

export const updateQuote = (quoteId: string, options: APQueryOptions = {}) => {
  const { fetchWithAuth } = useApi();
  const {
    defaultOnMutateError,
    defaultOnMutateSuccess,
    defaultOnMutateSettled,
    optimisiticUpdateOnMutate,
    getDataByQueryKey,
  } = useAPQuery();
  return useMutation({
    mutationFn: (payload: PatchQuotePayload): Promise<string> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { quoteComparisonIds, ..._payload } = payload;

      return fetchWithAuth<string>("updateQuote", quoteId, {
        requestOptions: { method: "PATCH", body: JSON.stringify(_payload) },
      });
    },
    onError: defaultOnMutateError(options),
    onSuccess: defaultOnMutateSuccess(options),
    onSettled: (data, error, payload) => {
      const invalidateList = [
        ["list-quote", quoteId],
        ["quote", quoteId],
        ["quote-vehicle-suggestions", quoteId],
        ["quote-vehicle-images", quoteId],
        ["quote-vehicle-info", quoteId],
        ["quote-calculations", quoteId],
        ["quote-finance-comparison-calculations", quoteId],
        ["quote-finance-comparison", quoteId],
      ];

      if (payload.dossierId) {
        invalidateList.push(
          ["quotes", payload.dossierId],
          ["dossier", payload.dossierId],
          ["quotes-with-specs", payload.dossierId]
        );
      }
      defaultOnMutateSettled(invalidateList);
    },
    onMutate: async (payload) => {
      // Update quote comparison query key
      if (payload.dossierId && payload.name && payload.quoteComparisonIds) {
        const quoteComparisonQueryKey = [
          "quotes-with-specs",
          payload.dossierId,
          payload.quoteComparisonIds,
        ];
        const quotes = getDataByQueryKey(
          quoteComparisonQueryKey
        ) as QuoteWithVehicleSpecification[];

        if (quotes) {
          const quoteIndex = quotes.findIndex(
            (quote) => quote.identifiers.crmQuoteId === quoteId
          );
          const quoteToUpdate = quotes[quoteIndex];
          const updatedQuotes = [...quotes];
          updatedQuotes[quoteIndex] = {
            ...quoteToUpdate,
            identifiers: {
              ...quoteToUpdate.identifiers,
              name: payload.name,
            },
          };

          optimisiticUpdateOnMutate(quoteComparisonQueryKey, updatedQuotes);
        }
      }
    },
  });
};
